import React, { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Divider, Flex } from '@televet/kibble-ui/build/chakra';
import ExclusionaryTimeSlotSection from './components/ExclusionaryTimeSlotSection';
import { useGetClinicDirectBookingSettingsQuery, useUpsertDirectBookingSettingsMutation } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { DirectBookingAppointmentIntervalInMinutes } from './components/DirectBookingAppointmentIntervalInMinutes';
import { AdvanceBookingLimitationsSection } from './components/AdvanceBookingLimitationsSection';

const DirectBookingSettings = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [createDirectBookingSettings] = useUpsertDirectBookingSettingsMutation();

  const { data: directBookingSettingsData, refetch: refetchDirectBookingSettings } =
    useGetClinicDirectBookingSettingsQuery({
      variables: {
        where: { clinicId: { equals: currentClinicId } },
      },
      skip: !currentClinicId,
      onCompleted: async (data) => {
        if (data.findFirstClinicDirectBookingSetting === null) {
          try {
            await createDirectBookingSettings({
              variables: {
                where: { id: directBookingSettingsId || '' },
                update: {},
                create: {
                  clinic: {
                    connect: { id: currentClinicId },
                  },
                },
              },
            });
            refetchDirectBookingSettings();
          } catch (e) {
            console.error('Error creating Direct Booking Setting', e);
            Sentry.captureException(`Unable to create Direct Booking Setting for clinic ${currentClinicId}`, e);
          }
        }
      },
    });

  // used if settings don't exist yet when toggled on
  const refetchInitialSettings = useCallback(() => {
    if (!directBookingSettingsData?.findFirstClinicDirectBookingSetting) {
      refetchDirectBookingSettings();
    }
  }, [directBookingSettingsData, refetchDirectBookingSettings]);

  const directBookingSettingsId = useMemo(
    () => directBookingSettingsData?.findFirstClinicDirectBookingSetting?.id,
    [directBookingSettingsData],
  );

  const exclusionsEnabled = useMemo(
    () => !!directBookingSettingsData?.findFirstClinicDirectBookingSetting?.exclusionRulesEnabled,
    [directBookingSettingsData],
  );
  const [updateDirectBookingSettings] = useUpsertDirectBookingSettingsMutation();
  return (
    <Flex direction="column">
      <DirectBookingAppointmentIntervalInMinutes
        directBookingSettingsData={directBookingSettingsData}
        updateDirectBookingSettings={updateDirectBookingSettings}
        refetchSettings={refetchDirectBookingSettings}
        clinicId={currentClinicId}
      />
      <Divider />
      <AdvanceBookingLimitationsSection
        directBookingSettingsData={directBookingSettingsData}
        updateDirectBookingSettings={updateDirectBookingSettings}
        refetchSettings={refetchDirectBookingSettings}
        clinicId={currentClinicId}
      />
      <Divider />
      <ExclusionaryTimeSlotSection
        clinicId={currentClinicId}
        directBookingSettingsId={directBookingSettingsId}
        exclusionsEnabled={exclusionsEnabled}
        refetchSettings={refetchInitialSettings}
        updateDirectBookingSettings={updateDirectBookingSettings}
      />
    </Flex>
  );
};

export default DirectBookingSettings;
